import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchManuals } from '../../../../api/fetchManuals';
import type { IAssetManuals } from '../../../../hooks/useAssetManuals';
import { useChassisCopy } from '../../../../hooks/useChassisCopy';
import './ManualTab.scss';

interface IManualTabProps {
    chassis: string;
}

const COPIED_FEEDBACK_MS = 2000;

const ManualTab = ({ chassis }: IManualTabProps) => {
    const intl = useIntl();
    const { copyChassis } = useChassisCopy();
    const [manuals, setManuals] = useState<IAssetManuals | null>(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setManuals(null);
        fetchManuals(chassis)
            .then(result => {
                if (!cancelled) {
                    setManuals(result ?? null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setManuals(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [chassis]);

    useEffect(() => {
        if (!copied) {
            return;
        }
        const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        if (await copyChassis(chassis)) {
            setCopied(true);
        }
    };

    const list = manuals?.manuals ?? [];

    return (
        <>
            <div className='fm-fieldrow'>
                <div className='fm-fgroup' style={{ flex: 2 }}>
                    <span>{intl.formatMessage({ id: 'intl-msg:monitoring.manual.chassisLabel' })}</span>
                    <button
                        type='button'
                        className='fm-copyfield'
                        onClick={handleCopy}
                        title={intl.formatMessage({ id: 'intl-msg:monitoring.manual.chassisTooltip' })}
                    >
                        <span className='fm-mono'>{chassis}</span>
                        <span className={`fm-copyfield__state${copied ? ' fm-copied' : ''}`}>
                            {copied ? (
                                <>
                                    <span className='rioglyph rioglyph-ok' />{' '}
                                    {intl.formatMessage({ id: 'intl-msg:monitoring.manual.itemCopied' })}
                                </>
                            ) : (
                                <span className='rioglyph rioglyph-duplicate' />
                            )}
                        </span>
                    </button>
                </div>
                <div className='fm-fgroup' style={{ flex: 1 }}>
                    <span>{intl.formatMessage({ id: 'intl-msg:monitoring.manual.yearLabel' })}</span>
                    <div className='fm-readfield fm-num'>{manuals?.year ?? '–'}</div>
                </div>
            </div>

            {loading && (
                <div className='fm-loading fm-loading--inline'>
                    <div className='fm-progress'>
                        <i />
                    </div>
                    <span>{intl.formatMessage({ id: 'intl-msg:monitoring.manual.searching' })}</span>
                </div>
            )}

            {!loading && list.length > 0 && (
                <table className='fm-minitable'>
                    <thead>
                        <tr>
                            <th>{intl.formatMessage({ id: 'intl-msg:monitoring.manual.tableHeaderName' })}</th>
                            <th className='fm-center'>
                                {intl.formatMessage({ id: 'intl-msg:monitoring.manual.tableHeaderDownload' })}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map(manual => (
                            <tr key={manual.downloadLink}>
                                <td>{manual.name}</td>
                                <td className='fm-center'>
                                    <a
                                        href={manual.downloadLink}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='fm-iconlink'
                                        aria-label={manual.name}
                                    >
                                        <span className='rioglyph rioglyph-cloud-download' />
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!loading && !list.length && (
                <p className='fm-muted'>{intl.formatMessage({ id: 'intl-msg:monitoring.manual.notFound' })}</p>
            )}
        </>
    );
};

export default ManualTab;
